import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Ban,
  Boxes,
  Building2,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  Copy,
  Droplets,
  ExternalLink,
  Eye,
  FileCheck2,
  FileDown,
  FilePlus2,
  FileText,
  Filter,
  GitFork,
  Hand,
  History,
  Home,
  Inbox,
  Layers,
  ListChecks,
  Lock,
  Package,
  PackageCheck,
  PackagePlus,
  Pause,
  Pencil,
  Play,
  Plus,
  Power,
  Printer,
  RefreshCw,
  RotateCcw,
  Save,
  ScanLine,
  Send,
  Settings2,
  ShieldAlert,
  ShieldCheck,
  SkipForward,
  Sparkles,
  Trash2,
  Truck,
  Unlock,
  Upload,
  UserRound,
  Users,
  Wallet,
  Wrench,
  X,
  XCircle,
  Clock,
  Tags,
} from "lucide-react";

/**
 * Shared Ops/Field action chrome icons.
 * Resolve by explicit `iconKey`, else by stable action `id`.
 */
export const PAGE_ACTION_ICONS: Record<string, LucideIcon> = {
  // Navigation / Views / Detail
  open: Eye,
  next: ArrowRight,
  view: Eye,
  detail: Eye,
  details: Eye,
  "view-detail": Eye,
  "open-to": Eye,

  // Field / MES / Batches
  batch: Layers,
  batches: Layers,
  "create-batch": Sparkles,
  sparkles: Sparkles,
  ebr: ClipboardCheck,
  "print-ebr": Printer,
  field: ScanLine,
  pause: Pause,
  hold: ShieldAlert,
  cancel: Ban,
  cip: Droplets,
  ipqc: ClipboardCheck,
  scan: ScanLine,
  today: Home,
  handover: Hand,
  start: Play,
  resume: SkipForward,
  complete: CheckCircle2,
  mo: FileText,
  "create-mo": FileText,
  fileText: FileText,
  clipboardCheck: ClipboardCheck,

  // Losses / Defects / QC
  loss: AlertTriangle,
  losses: AlertTriangle,
  "loss-types": AlertTriangle,
  defect: AlertCircle,
  defects: AlertCircle,
  reason: FileText,
  reasons: FileText,
  masters: FileText,
  iqc: ClipboardCheck,
  fqc: ShieldCheck,
  ncr: ShieldAlert,
  qc: ShieldCheck,
  "qc-plan": ShieldCheck,
  pass: CheckCircle2,
  fail: XCircle,
  release: ShieldCheck,

  // Warehouse / Inventory / Lots
  lot: Boxes,
  lots: Boxes,
  move: ArrowRight,
  pick: Hand,
  reserve: Boxes,
  "reserve-fefo": Boxes,
  boxes: Boxes,
  package: Package,
  "create-gi": PackagePlus,
  "create-shipment": Truck,
  gr: PackageCheck,
  receipt: FileText,
  "gr-po": PackageCheck,
  receive: PackageCheck,
  dispose: Wrench,

  // Ops CRUD / workflow
  create: Plus,
  add: Plus,
  edit: Pencil,
  adjust: Settings2,
  "adjust-voucher": Settings2,
  save: Save,
  delete: Trash2,
  back: ArrowLeft,
  approve: CheckCircle2,
  reject: XCircle,
  submit: Send,
  "submit-hint": Send,
  convert: PackageCheck,
  "convert-hint": PackageCheck,
  confirm: CheckCircle2,
  close: Ban,
  dismiss: X,
  post: Send,
  print: Printer,
  "export-pdf": FileDown,
  "export-excel": FileDown,
  export: FileDown,
  draft: FilePlus2,
  effective: BadgeCheck,
  readiness: ListChecks,
  conditional: FileCheck2,
  reset: RotateCcw,

  // Status / Toggle
  status: Power,
  toggle: Power,
  "toggle-status": Power,

  // Engineering / MDM aliases
  routing: GitFork,
  bom: Boxes,
  spec: FileText,
  pack: PackagePlus,
  packs: PackagePlus,
  "add-pack": PackagePlus,
  "create-bom": Plus,
  "create-routing": Plus,
  "create-spec": Plus,
  plant: Building2,
  warehouse: Home,
  location: Boxes,
  locations: Boxes,
  line: Layers,
  shift: Layers,
  area: Layers,

  // Nav / hubs / General
  inbox: Inbox,
  policies: Settings2,
  orders: ClipboardList,
  copy: Copy,
  history: History,
  audit: History,
  download: FileDown,
  upload: Upload,
  refresh: RefreshCw,
  sync: RefreshCw,
  link: ExternalLink,
  lock: Lock,
  unlock: Unlock,
  user: UserRound,
  users: Users,
  partner: Building2,
  customer: UserRound,
  "mark-all": CheckCircle2,
  "filter-all": Filter,
  "filter-unread": Filter,

  // Partners — credit / debt
  "propose-credit": Wallet,
  wallet: Wallet,
  "propose-debt": Tags,
  tags: Tags,
  waiting: Clock,
  clock: Clock,
};

/** Short chrome labels — page/row context supplies domain meaning. */
const SHORT_LABEL_BY_ID: Record<string, string> = {
  create: "Tạo",
  edit: "Sửa",
  view: "Xem chi tiết",
  open: "Xem chi tiết",
  detail: "Xem chi tiết",
  save: "Lưu",
  delete: "Xóa",
  back: "Danh sách",
  approve: "Duyệt",
  reject: "Từ chối",
  submit: "Gửi",
  release: "Phát hành",
  reserve: "Khoanh giữ",
  "reserve-fefo": "FEFO",
  confirm: "Xác nhận",
  receive: "Nhận",
  close: "Đóng",
  dismiss: "Đóng",
  cancel: "Hủy",
  post: "Ghi sổ",
  pass: "Đạt",
  fail: "Không đạt",
  draft: "Nháp",
  effective: "Hiệu lực",
  dispose: "Xử lý",
  hold: "HOLD",
  pause: "Tạm dừng",
  readiness: "Sẵn sàng",
  conditional: "Có điều kiện",
  inbox: "Hộp thư",
  policies: "Chính sách",
  orders: "MO",
  mo: "Mở MO",
  ebr: "Hồ sơ mẻ",
  "print-ebr": "In hồ sơ mẻ",
  iqc: "IQC",
  ncr: "NCR",
  today: "Hôm nay",
  scan: "Quét",
  cip: "CIP",
  ipqc: "IPQC",
  "create-gi": "Xuất kho",
  "create-shipment": "Đóng gói",
  "create-batch": "Mẻ",
  batch: "Mẻ SX",
  lot: "Chi tiết lô",
  lots: "Xem lô",
  "add-pack": "Quy cách",
  "create-bom": "BOM",
  "create-routing": "Routing",
  "create-spec": "Tiêu chuẩn kỹ thuật",
  "toggle-status": "Trạng thái",
  status: "Trạng thái",
  "mark-all": "Đã đọc",
  "gr-po": "GR từ PO",
  "open-to": "Mở",
  "submit-hint": "Gửi duyệt",
  convert: "Tạo đơn mua",
  "convert-hint": "Tạo đơn mua",
  print: "In",
  "export-pdf": "PDF",
  reset: "Reset",
};

export type ResolveActionIconOptions = {
  /** If true and no matching icon is resolved, fall back to defaultIcon or Eye. */
  fallback?: boolean;
  defaultIcon?: LucideIcon;
};

export function resolveActionIcon(
  id: string,
  iconKey?: string,
  className = "size-4 shrink-0",
  label?: string,
  options?: ResolveActionIconOptions,
): ReactNode {
  const key = (iconKey ?? id).toLowerCase().trim();
  let Icon = PAGE_ACTION_ICONS[key];

  if (!Icon) {
    if (
      key.startsWith("view") ||
      key.endsWith("view") ||
      key.includes("detail") ||
      key.includes("open")
    ) {
      Icon = Eye;
    } else if (key.startsWith("edit") || key.endsWith("edit")) {
      Icon = Pencil;
    } else if (
      key.startsWith("delete") ||
      key.endsWith("delete") ||
      key.includes("remove")
    ) {
      Icon = Trash2;
    } else if (
      key.startsWith("create") ||
      key.endsWith("create") ||
      key.includes("add")
    ) {
      Icon = Plus;
    } else if (key.includes("batch")) {
      Icon = Layers;
    } else if (key.includes("lot")) {
      Icon = Boxes;
    } else if (key.includes("status") || key.includes("toggle")) {
      Icon = Power;
    } else if (key.includes("print")) {
      Icon = Printer;
    } else if (key.includes("ebr")) {
      Icon = ClipboardCheck;
    } else if (key.includes("loss")) {
      Icon = AlertTriangle;
    } else if (key.includes("defect") || key.includes("ncr")) {
      Icon = ShieldAlert;
    } else if (key.includes("move")) {
      Icon = ArrowRight;
    } else if (key.includes("pick")) {
      Icon = Hand;
    } else if (key.includes("reserve")) {
      Icon = Boxes;
    } else if (
      key.includes("order") ||
      key.includes("mo") ||
      key.includes("po")
    ) {
      Icon = FileText;
    } else if (key.includes("reset")) {
      Icon = RotateCcw;
    } else if (key.includes("field")) {
      Icon = ScanLine;
    }
  }

  if (!Icon && label) {
    const actionId = resolveActionIdFromLabel(label);
    if (actionId && PAGE_ACTION_ICONS[actionId]) {
      Icon = PAGE_ACTION_ICONS[actionId];
    }
  }

  if (!Icon && options?.fallback) {
    Icon = options.defaultIcon ?? Eye;
  }

  if (!Icon) return null;
  return <Icon className={className} aria-hidden strokeWidth={2.25} />;
}

export function resolveActionLabel(id: string, fallback: string): string {
  return SHORT_LABEL_BY_ID[id] ?? fallback;
}

/** Heuristic for form/dialog CTAs that only expose a label string. */
export function resolveActionIdFromLabel(label: string): string | undefined {
  const l = label.trim().toLowerCase();
  if (!l) return undefined;
  if (l === "tiếp" || l === "tiếp tục" || l === "next") return "next";
  if (l === "quay lại" || l === "back") return "back";

  // Specific compound phrases
  if (l.includes("ebr") || l.includes("hồ sơ mẻ")) return "ebr";
  if (
    l.includes("chọn lô") ||
    l.includes("chọn thủ công") ||
    l.startsWith("chọn ") ||
    l === "chọn" ||
    l.includes("pick")
  )
    return "pick";
  if (
    l.includes("tạo đơn mua") ||
    l.includes("chuyển thành po") ||
    l.includes("chuyển po")
  )
    return "convert";
  if (
    l.includes("chuyển vị trí") ||
    l.includes("chuyển kho") ||
    l.includes("chuyển ")
  )
    return "move";
  if (
    l.includes("tiêu hủy") ||
    l.includes("tiêu huỷ") ||
    l.includes("xử lý") ||
    l.includes("dispose")
  )
    return "dispose";
  if (l.includes("khoanh giữ") || l.includes("giữ chỗ") || l.includes("fefo") || l.includes("reserve"))
    return "reserve";
  if (l.includes("gửi duyệt")) return "submit";
  if (l.includes("chờ phê duyệt") || l.startsWith("chờ ")) return "waiting";
  if (l.includes("hạn mức tín dụng") || l.includes("hạn mức"))
    return "propose-credit";
  if (
    l.includes("phân khúc công nợ") ||
    l.includes("phân khúc") ||
    (l.includes("công nợ") && l.includes("tạo"))
  )
    return "propose-debt";
  if (
    l.includes("lệnh sx") ||
    l.includes("tạo lệnh") ||
    l.includes("lệnh sản xuất") ||
    l.includes("mở mo")
  )
    return "mo";
  if (l.includes("mẻ") || l.includes("batch")) return "batch";
  if (l.includes("lô") || l.includes("lot")) return "lot";
  if (
    l.includes("chi tiết") ||
    l.startsWith("xem") ||
    l === "xem" ||
    l === "mở" ||
    l.startsWith("mở ") ||
    l === "view" ||
    l === "open"
  )
    return "view";
  if (l === "nhân bản" || l.includes("sao chép") || l === "copy") return "copy";

  // CRUD & Lifecycle
  if (l === "hủy" || l.startsWith("hủy ") || l === "cancel") return "cancel";
  if (l === "đóng" || l === "close" || l === "dismiss") return "dismiss";
  if (l === "lưu" || l.startsWith("lưu ") || l === "save") return "save";
  if (
    l.startsWith("tạo ") ||
    l === "tạo" ||
    l.startsWith("thêm ") ||
    l === "thêm" ||
    l === "create" ||
    l === "add"
  )
    return "create";
  if (l.includes("sửa") || l.includes("chỉnh sửa") || l === "edit") return "edit";
  if (l.includes("điều chỉnh") || l === "adjust") return "adjust";
  if (l.includes("xóa") || l.includes("xoá") || l === "delete") return "delete";
  if (l === "reset" || l.includes("làm lại")) return "reset";
  if (
    l === "bật" ||
    l === "tắt" ||
    l === "ngưng" ||
    l === "kích hoạt" ||
    l.includes("trạng thái") ||
    l.includes("toggle") ||
    l.includes("status")
  )
    return "status";
  if (l === "duyệt" || l.startsWith("duyệt ") || l.includes("phê duyệt") || l === "approve") return "approve";
  if (l === "từ chối" || l === "reject") return "reject";
  if (
    l === "gửi" ||
    l.startsWith("gửi ") ||
    l === "submit"
  )
    return "submit";
  if (
    l === "xác nhận" ||
    l === "confirm" ||
    l.startsWith("xác nhận ") ||
    l.includes("hoàn tất") ||
    l === "complete"
  )
    return "confirm";
  if (l === "bàn giao" || l === "handover") return "handover";
  if (l === "đạt" || l === "pass") return "pass";
  if (l === "không đạt" || l === "fail") return "fail";
  if (l.includes("phát hành") || l.includes("release")) return "release";
  if (l.includes("ghi sổ") || l === "post") return "post";
  if (
    l.includes("in phiếu") ||
    l === "in" ||
    l.startsWith("in ") ||
    l.includes("print")
  )
    return "print";
  if (l.includes("xuất pdf") || l === "pdf") return "export-pdf";
  if (l.includes("đóng gói") || l.includes("packing") || l.includes("shipment"))
    return "create-shipment";
  if (l.includes("xuất kho")) return "create-gi";
  if (l.includes("hiệu lực") || l.includes("effective")) return "effective";
  if (l.includes("nháp") || l.includes("draft")) return "draft";
  if (l.includes("move")) return "move";
  if (l.includes("field") || l.includes("thực thi")) return "field";
  if (l.includes("hao hụt") || l.includes("loss")) return "losses";
  if (
    l.includes("lỗi") ||
    l.includes("khuyết tật") ||
    l.includes("không phù hợp") ||
    l.includes("defect") ||
    l.includes("ncr")
  )
    return "ncr";
  return undefined;
}
